import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Building2, Mail, Plus, Save, Search } from "lucide-react";
import { Button, EmptyState, ErrorBanner, Field, Input, LoadingState, PageHeader, Panel, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import { requireSupabase } from "../lib/supabase";
import type { SupplierRow } from "../types";

const DEFAULT_SUBJECT = "Retourenanfrage Auftrag {auftragsnummer}";
const DEFAULT_BODY = `Guten Tag,

wir möchten folgende Artikel zur Retoure anfragen.

Unser Auftrag: {auftragsnummer}
Lieferscheinnummer: {lieferscheinnummer}
Lager: {lager}

Retourenpositionen:
{artikel}

Grund / Hinweis:
{hinweis}

Bitte teilen Sie uns die Retourennummer und das weitere Vorgehen mit.

Mit freundlichen Grüßen
K&S Gebäudetechnik`;

type SupplierDraft = Omit<SupplierRow, "id">;

const emptyDraft = (): SupplierDraft => ({
  name: "",
  return_email: null,
  contact_name: null,
  customer_number: null,
  phone: null,
  street: null,
  postal_code: null,
  city: null,
  notes: null,
  return_email_subject_template: DEFAULT_SUBJECT,
  return_email_body_template: DEFAULT_BODY,
  is_active: true
});

const demoSuppliers: SupplierRow[] = [
  { id: "demo-unielektro", ...emptyDraft(), name: "Unielektro", return_email: "retoure@unielektro.de", contact_name: "Retourenabteilung", customer_number: "1156442" },
  { id: "demo-baupart", ...emptyDraft(), name: "Baupart", return_email: "retoure@baupart.de" }
];

const optional = (value: string | null) => value?.trim() || null;
const toDraft = ({ id: _id, ...supplier }: SupplierRow): SupplierDraft => supplier;

export function SuppliersPage() {
  const { cloudConfigured, profile } = useAuth();
  const canManage = cloudConfigured && (profile?.role === "admin" || profile?.role === "dispatcher");
  const [suppliers, setSuppliers] = useState<SupplierRow[]>(demoSuppliers);
  const [selectedId, setSelectedId] = useState<string | null>(demoSuppliers[0].id);
  const [draft, setDraft] = useState<SupplierDraft>(toDraft(demoSuppliers[0]));
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(cloudConfigured);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async (preferredId?: string | null) => {
    if (!cloudConfigured) return;
    setLoading(true);
    const result = await requireSupabase().from("suppliers")
      .select("id,name,return_email,contact_name,customer_number,phone,street,postal_code,city,notes,return_email_subject_template,return_email_body_template,is_active")
      .order("name");
    if (result.error) {
      setError(`Lieferanten konnten nicht geladen werden: ${result.error.message}`);
      setLoading(false);
      return;
    }
    const next = (result.data ?? []) as SupplierRow[];
    setSuppliers(next);
    const nextId = preferredId && next.some((item) => item.id === preferredId)
      ? preferredId
      : next[0]?.id ?? null;
    setSelectedId(nextId);
    setDraft(nextId ? toDraft(next.find((item) => item.id === nextId)!) : emptyDraft());
    setError("");
    setLoading(false);
  }, [cloudConfigured]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return suppliers;
    return suppliers.filter((supplier) => [supplier.name, supplier.customer_number, supplier.city, supplier.return_email]
      .some((value) => value?.toLowerCase().includes(normalized)));
  }, [query, suppliers]);

  function selectSupplier(supplier: SupplierRow) {
    setSelectedId(supplier.id);
    setDraft(toDraft(supplier));
    setError("");
    setSuccess("");
  }

  function startNewSupplier() {
    setSelectedId(null);
    setDraft(emptyDraft());
    setError("");
    setSuccess("");
  }

  function patch(values: Partial<SupplierDraft>) {
    setDraft((current) => ({ ...current, ...values }));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const name = draft.name.trim();
    const email = optional(draft.return_email)?.toLowerCase() ?? null;
    const subject = draft.return_email_subject_template.trim();
    const body = draft.return_email_body_template.trim();
    if (!name) return setError("Der Lieferantenname ist erforderlich.");
    if (!subject || !body) return setError("Betreff und E-Mail-Text dürfen nicht leer sein.");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Die Retouren-E-Mail-Adresse ist ungültig.");

    const payload = {
      name,
      return_email: email,
      contact_name: optional(draft.contact_name),
      customer_number: optional(draft.customer_number),
      phone: optional(draft.phone),
      street: optional(draft.street),
      postal_code: optional(draft.postal_code),
      city: optional(draft.city),
      notes: optional(draft.notes),
      return_email_subject_template: subject,
      return_email_body_template: body,
      is_active: draft.is_active
    };
    setBusy(true);
    setError("");
    setSuccess("");
    const result = selectedId
      ? await requireSupabase().from("suppliers").update(payload).eq("id", selectedId).select("id").single()
      : await requireSupabase().from("suppliers").insert(payload).select("id").single();
    if (result.error) setError(result.error.message);
    else {
      setSuccess(selectedId ? "Lieferant wurde gespeichert." : "Lieferant wurde angelegt.");
      await load(result.data.id);
    }
    setBusy(false);
  }

  return (
    <>
      <PageHeader
        title="Lieferanten"
        description="Kontaktdaten, Retourenadresse und E-Mail-Vorlagen zentral verwalten."
        action={canManage ? <Button onClick={startNewSupplier}><Plus size={16} /> Lieferant hinzufügen</Button> : undefined}
      />
      {!cloudConfigured && <div className="notice-banner">Lokale Vorschau mit neutralen Beispieldaten.</div>}
      {error && <ErrorBanner message={error} />}
      {success && <div className="success-banner" role="status">{success}</div>}
      {loading ? <LoadingState label="Lieferanten werden geladen …" /> : (
        <div className="supplier-workspace">
          <Panel title={`${suppliers.length} Lieferanten`} description="Händler auswählen oder neu anlegen.">
            <div className="supplier-search"><Search size={16} /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Name, Kundennummer oder Ort suchen" /></div>
            <div className="supplier-list">
              {filtered.length === 0 ? <EmptyState title="Kein Lieferant gefunden" description="Bitte einen anderen Suchbegriff verwenden." /> : filtered.map((supplier) => (
                <button type="button" key={supplier.id} className={`supplier-list__item ${selectedId === supplier.id ? "supplier-list__item--active" : ""}`} onClick={() => selectSupplier(supplier)}>
                  <span><Building2 size={18} /></span>
                  <span><strong>{supplier.name}</strong><small>{supplier.return_email || "Keine Retouren-E-Mail"}</small></span>
                  <StatusBadge tone={supplier.is_active ? "success" : "neutral"}>{supplier.is_active ? "Aktiv" : "Inaktiv"}</StatusBadge>
                </button>
              ))}
            </div>
          </Panel>

          <form onSubmit={save}>
            <Panel title={selectedId ? draft.name || "Lieferant bearbeiten" : "Neuer Lieferant"} description="Diese Angaben stehen anschließend in beiden Lager-Apps zur Verfügung." action={<Mail size={21} />}>
              <div className="supplier-form-grid">
                <Field label="Lieferantenname"><Input value={draft.name} onChange={(event) => patch({ name: event.target.value })} required disabled={!canManage || busy} /></Field>
                <Field label="Kundennummer bei Lieferant"><Input value={draft.customer_number ?? ""} onChange={(event) => patch({ customer_number: event.target.value })} disabled={!canManage || busy} /></Field>
                <Field label="Ansprechpartner"><Input value={draft.contact_name ?? ""} onChange={(event) => patch({ contact_name: event.target.value })} disabled={!canManage || busy} /></Field>
                <Field label="Telefon"><Input value={draft.phone ?? ""} onChange={(event) => patch({ phone: event.target.value })} disabled={!canManage || busy} /></Field>
                <Field label="Straße"><Input value={draft.street ?? ""} onChange={(event) => patch({ street: event.target.value })} disabled={!canManage || busy} /></Field>
                <Field label="PLZ"><Input value={draft.postal_code ?? ""} onChange={(event) => patch({ postal_code: event.target.value })} disabled={!canManage || busy} /></Field>
                <Field label="Ort"><Input value={draft.city ?? ""} onChange={(event) => patch({ city: event.target.value })} disabled={!canManage || busy} /></Field>
                <Field label="Retouren-E-Mail" hint="Die Lager-App verwendet ausschließlich diese Adresse."><Input type="email" value={draft.return_email ?? ""} onChange={(event) => patch({ return_email: event.target.value })} placeholder="retoure@lieferant.de" disabled={!canManage || busy} /></Field>
              </div>
              <div className="supplier-template-section">
                <Field label="E-Mail-Betreff"><Input value={draft.return_email_subject_template} onChange={(event) => patch({ return_email_subject_template: event.target.value })} disabled={!canManage || busy} required /></Field>
                <Field label="E-Mail-Text" hint="Verfügbare Platzhalter: {auftragsnummer}, {lieferscheinnummer}, {lager}, {artikel}, {hinweis}">
                  <textarea className="input supplier-template-textarea" value={draft.return_email_body_template} onChange={(event) => patch({ return_email_body_template: event.target.value })} disabled={!canManage || busy} required />
                </Field>
                <Field label="Interne Notizen"><textarea className="input supplier-notes" value={draft.notes ?? ""} onChange={(event) => patch({ notes: event.target.value })} disabled={!canManage || busy} /></Field>
                <label className="supplier-active"><input type="checkbox" checked={draft.is_active} onChange={(event) => patch({ is_active: event.target.checked })} disabled={!canManage || busy} /><span>Lieferant ist aktiv und in der App auswählbar</span></label>
              </div>
              {canManage && <div className="supplier-form-actions"><Button type="submit" disabled={busy}><Save size={16} /> Speichern</Button></div>}
            </Panel>
          </form>
        </div>
      )}
    </>
  );
}
